import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import tokens from "../../../shared/theme/tokens";
import { isPlainLeftClick, resolveBannerHref } from "../bannerLink";

// Single source for the banner CDN host (also referenced by the property
// landing screen; the admin app and API hold their own copies of this URL).
export const BANNER_CDN = "https://d2b0f54uipcwhi.cloudfront.net";

export interface PropertyBannerProps {
  abbreviation: string;
  variant: "home" | "landing";
  bannerUpdatedAt?: string | null;
  homeBannerUpdatedAt?: string | null;
  /** Bundled fallback (landing variant) shown when no banner exists. */
  defaultImage?: string;
  /**
   * Optional click-through destination. When set, the banner is wrapped in a
   * link. Same-origin destinations (e.g. "/rent/reporting/home") navigate
   * in-app in the same tab; external URLs open in a new tab. Absent/empty =
   * plain image.
   */
  link?: string | null;
  sx?: object;
}

const bannerUrl = (abbreviation: string, home: boolean, ts?: string | null) =>
  `${BANNER_CDN}/banners/${abbreviation}${home ? "-home" : ""}.png${
    ts ? `?t=${new Date(ts).getTime()}` : ""
  }`;

/**
 * Property banner with the canonical fallback chains:
 * - variant="home":    {abbr}-home.png → {abbr}.png → renders nothing
 * - variant="landing": {abbr}.png → defaultImage (contained) → nothing
 *
 * Home renders as a full-width 4:1 strip; landing fills its parent (the
 * split-hero panel controls the shape).
 */
export const PropertyBanner = ({
  abbreviation,
  variant,
  bannerUpdatedAt,
  homeBannerUpdatedAt,
  defaultImage,
  link,
  sx,
}: PropertyBannerProps) => {
  const navigate = useNavigate();

  // 0 = preferred source, 1 = fallback source, 2 = exhausted
  const [stage, setStage] = useState(0);

  // New property (or fresh upload timestamps) → retry from the top.
  useEffect(() => {
    setStage(0);
  }, [abbreviation, bannerUpdatedAt, homeBannerUpdatedAt]);

  let src: string | undefined;
  let contained = false;

  if (variant === "home") {
    if (stage === 0) {
      src = bannerUrl(
        abbreviation,
        true,
        homeBannerUpdatedAt ?? bannerUpdatedAt
      );
    } else if (stage === 1) {
      src = bannerUrl(abbreviation, false, bannerUpdatedAt);
    }
  } else {
    if (stage === 0) {
      src = bannerUrl(abbreviation, false, bannerUpdatedAt);
    } else if (stage === 1 && defaultImage) {
      src = defaultImage;
      contained = true;
    }
  }

  if (!src) return null;

  const isStrip = variant === "home";

  // Only the real property banner (not the bundled fallback) is linkable.
  const { href, internalPath } = resolveBannerHref(contained ? null : link);

  // Same-origin destinations navigate in-app in the same tab; external ones
  // open in a new tab. A real href is kept in both cases for accessibility
  // and right-click / open-in-new-tab.
  const linkProps: Record<string, unknown> = internalPath
    ? {
        onClick: (e: React.MouseEvent) => {
          if (!isPlainLeftClick(e)) return;
          e.preventDefault();
          navigate(internalPath);
        },
      }
    : { target: "_blank", rel: "noopener noreferrer" };

  const image = (
    <img
      src={src}
      alt={`${abbreviation} banner`}
      style={{
        width: "100%",
        height: "100%",
        objectFit: contained ? "contain" : "cover",
        display: "block",
      }}
      onError={() => setStage(stage + 1)}
    />
  );

  return (
    <Box
      sx={{
        width: "100%",
        ...(isStrip
          ? {
              aspectRatio: "4 / 1",
              borderRadius: `${tokens.radius.md}px`,
              overflow: "hidden",
              mb: 3,
            }
          : { height: "100%" }),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...sx,
      }}
    >
      {href ? (
        <Box
          component="a"
          href={href}
          {...linkProps}
          sx={{
            display: "block",
            width: "100%",
            height: "100%",
            lineHeight: 0,
          }}
        >
          {image}
        </Box>
      ) : (
        image
      )}
    </Box>
  );
};
